import {
  ACCESS_TOKEN_COOKIE,
  clearSessionCookies,
  readCookie,
} from "@/lib/session";
import { retryingFetch } from "@/lib/http";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8080";

// Any JSON object or array; interfaces are not assignable to Record<string, unknown>.
type Json = object;

export class APIError extends Error {
  meta?: Record<string, unknown>;
  status?: number;
  /** Machine-readable code (the response's `error`), e.g. "feed_limit_reached". */
  code?: string;
  /** Human sentence (the response's `message`) when the API sends one. */
  detail?: string;
  constructor(message: string, meta?: Record<string, unknown>, status?: number, code?: string, detail?: string) {
    super(message);
    this.name = "APIError";
    this.meta = meta;
    this.status = status;
    this.code = code;
    this.detail = detail;
  }
}

/** The text to show a user for a failed request: the API's sentence if it sent one. */
export function apiErrorText(err: unknown, fallback: string): string {
  if (err instanceof APIError) return err.detail || err.message || fallback;
  return err instanceof Error && err.message ? err.message : fallback;
}

function handleUnauthorizedRedirect(status?: number) {
  if (typeof window === "undefined") return;

  if (status === 401) {
    clearSessionCookies();
    window.location.href = "/login";
  }
}

function handleSubscriptionRequiredRedirect(status?: number, billingURL?: string) {
  if (typeof window === "undefined" || status !== 402) return;

  const target = billingURL?.startsWith("/") && !billingURL.startsWith("//")
    ? billingURL
    : "/account?tab=billing";
  if (`${window.location.pathname}${window.location.search}` === target) return;

  window.history.replaceState(null, "", target);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const accessToken = readCookie(ACCESS_TOKEN_COOKIE);
  if (!accessToken) {
    handleUnauthorizedRedirect(401);
    throw new Error("Unauthorized: missing access token. Please sign in from the main dashboard.");
  }
  const headers = new Headers(init?.headers);
  headers.set("Authorization", `Bearer ${accessToken}`);
  // retryingFetch transparently absorbs cold-start / transient transport failures
  // (502/503/504, timeouts, connection resets) with backoff so they never surface.
  return retryingFetch(`${API_URL}${path}`, { ...init, headers });
}

type ErrorBody = { error?: string; message?: string; errorMeta?: Record<string, unknown>; billing_url?: string };

/** Turns a failed response into an APIError, applying the 401/402 redirects. */
async function failure(res: Response): Promise<APIError> {
  handleUnauthorizedRedirect(res.status);
  let message = `API ${res.status}`;
  let meta: Record<string, unknown> | undefined;
  let code: string | undefined;
  let detail: string | undefined;
  try {
    const body = (await res.json()) as ErrorBody;
    message = body.error || body.message || message;
    meta = body.errorMeta;
    code = body.error;
    detail = body.message;
    handleSubscriptionRequiredRedirect(res.status, body.billing_url);
  } catch {
    // ignore json parse errors and keep default message
    handleSubscriptionRequiredRedirect(res.status);
  }
  return new APIError(message, meta, res.status, code, detail);
}

async function getJSON<T extends Json>(path: string): Promise<T> {
  const res = await apiFetch(path);
  if (!res.ok) throw await failure(res);
  return (await res.json()) as T;
}

async function sendJSON<T extends Json>(path: string, method: string, body?: unknown): Promise<T> {
  const res = await apiFetch(path, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw await failure(res);
  return (await res.json()) as T;
}

export interface FindingsResponse {
  findings: Array<{
    id: number;
    finding_number: number;
    detector_id: string;
    severity: "info" | "warning" | "alert" | "critical";
    message: string;
    metadata?: string;
    transaction_ids?: string[];
    evidence_type?: "Transactions" | "Graph" | "Text";
    finding_state: string;
    is_resolved: boolean;
    created_at: string;
  }>;
  total: number;
}

export interface FindingsSummaryResponse {
  total: number;
  active: number;
  resolved: number;
}

export interface TransactionsResponse {
  transactions: Array<{
    id: string;
    date: string;
    payee: string;
    category_id: string;
    category_name?: string;
    amount: number;
    account_name: string;
    tags: Array<{ normalized_name: string }>;
    /** Who set the category: provider, mapping, user or rule. */
    category_source?: string;
    /** True in the "Deleted" view. */
    deleted?: boolean;
    /** Transaction in another currency than yours: what it was (milli-units, in `currency`) and the rate used. `amount` is already converted. */
    original_amount?: number;
    currency?: string;
    fx_rate?: string;
    /** The bank's own text, sent by bank feeds; for budgeting apps it repeats the memo. */
    description?: string;
    memo?: string;
  }>;
  total: number;
}

export interface ClassifiedTransactionsResponse {
  transactions: Array<{
    id: string;
    date: string;
    payee: string;
    category_id: string;
    category_name?: string;
    amount: number;
    account_name: string;
  }>;
  total: number;
}

export interface PayeesResponse {
  payees: Array<{
    id: string;
    global_payee_id: string;
    display_name: string;
    global_canonical_name?: string;
    is_safe_effective: boolean;
    is_safe_global: boolean;
    is_safe_override?: boolean | null;
    is_watched: boolean;
    complaints_count_local: number;
    tx_count_local: number;
    created_at: string;
  }>;
  total: number;
  counts: {
    all: number;
    safe: number;
    flagged: number;
    watched: number;
  };
}

export interface DetectorPref {
  detector_id: string;
  can_invoke: boolean;
  can_notify: boolean;
}

export interface DetectorMeta {
  id: string;
  name?: string;
  description?: string;
  schedule?: string;
  severity?: "info" | "warning" | "alert" | "critical";
  tier?: string;
  notify_locked?: boolean;
  /** Needs budgeted/available amounts, which only a budgeting app provides. */
  requires_budget_app?: boolean;
}

export interface DetectorGroupItem {
  group_key: string;
  group_name: string;
  group_description: string;
  detectors: DetectorMeta[];
}

export interface FamilyResponse {
  members: Array<{
    name: string;
    email: string;
    role: "owner" | "member";
  }>;
}

export interface InvitePreviewResponse {
  invited_email: string;
  expires_at: string;
  accepted: boolean;
}

export interface ReferralStats {
  referral_link: string;
  total_sent: number;
  conversions: number;
  free_months: number;
}

export interface Invoice {
  id: string;
  date: string;
  amount: number;
  status: string;
  pdf_url: string;
}

export interface AgencyContext {
  agency_name: string;
  tier: "FREE" | "PRO" | "PREMIUM";
  subscription_status: "trial" | "trial_expired" | "monthly" | "yearly" | "beta" | "expired";
  trial_ends_at?: string;
  subscription_expires_at?: string;
  subscription_cancel_at_period_end?: boolean;
  subscription_canceled_at?: string;
  agency_role: "owner" | "member";
  user_role: "user" | "admin";
}

export interface TransactionAggregateItem {
  key: string;
  label: string;
  count: number;
  total_amount: number;
  inflow_amount: number;
  outflow_amount: number;
}


export interface CategoryAllocation {
  selected_plan: "customize" | "save" | "balance" | "enjoy";
  origin: "user" | "ai";
  allocations: Record<string, number>;
}

export interface CategoryGroupOverview {
  group: string;
  amount: number;
  pct: number;
}

export interface ContactPayload {
  first_name: string;
  last_name?: string;
  email: string;
  category: string;
  message: string;
}

export interface NotificationPreferences {
  new_findings: boolean;
  weekly_digest: boolean;
  weekly_ai_summary: boolean;
  monthly_ai_summary: boolean;
  summary_timezone: string;
  summary_weekday: "Sunday" | "Monday";
  summary_time: string;
  product_updates: boolean;
  updated_at: string;
}

export interface FormattingPreferences {
  currency: string;
  currency_placement: "before" | "after" | "none";
  number_format: "us" | "eu";
  date_format: "mmm_d_yyyy" | "yyyy_mm_dd" | "dd_mm_yyyy" | "mm_dd_yyyy" | "d_mmm_yyyy";
  hide_amounts: boolean;
}

export interface UserOnboardingStatus {
  completed: boolean;
  completed_at?: string;
}

export interface DashboardSource {
  provider: ConnectionProvider;
  kind: ConnectionKind;
  label: string;
  status: ConnectionStatus;
  last_synced_at?: string | null;
}

export interface DashboardSummaryResponse {
  /** Every connected source: YNAB, Lunch Money and bank feeds. */
  sources: DashboardSource[];
  /** Direct bank sync only: what the user still has to do. */
  ledger: { needs_category: number };
  findings: {
    total: number;
    active: number;
    resolved: number;
    last_investigation_at?: string | null;
  };
  ynab: {
    connected: boolean;
    last_synced_at?: string | null;
    budget_label: string;
  };
  insights: {
    flagged_payees: number;
    total_payees: number;
    recurring_payees: number;
  };
  automation: {
    enabled_detectors: number;
    total_detectors: number;
    new_findings_enabled: boolean;
    enabled_loud_detectors: number;
    loud_notify_count: number;
  };
  score: {
    value: number | null;
  };
}

export interface MoneyFlowSummaryResponse {
  start_date: string;
  end_date: string;
  totals: {
    salary_total: number;
    income_total: number;
    expense_total: number;
    net_total: number;
    months_count: number;
  };
  groups: Array<{
    category_group_id: string;
    category_group: string;
    is_income: boolean;
    total_activity: number;
    total_budgeted: number;
    total_balance: number;
  }>;
  income_payees: Array<{
    Payee: string;
    TotalAmount: number;
  }>;
  categories: Array<{
    CategoryID: string;
    CategoryName: string;
    CategoryGroupID: string;
    CategoryGroup: string;
    IsIncome: boolean;
    TotalActivity: number;
    TotalBudgeted: number;
    TotalBalance: number;
  }>;
}

export async function fetchFindings(
  limit = 200,
  offset = 0,
  filters?: {
    from?: string;
    to?: string;
    status?: "all" | "active" | "resolved";
    severity?: "info" | "warning" | "alert" | "critical";
    detectorID?: string;
  }
): Promise<FindingsResponse> {
  const query = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  if (filters?.from) query.set("from", filters.from);
  if (filters?.to) query.set("to", filters.to);
  if (filters?.status) query.set("status", filters.status);
  if (filters?.severity) query.set("severity", filters.severity);
  if (filters?.detectorID) query.set("detector_id", filters.detectorID);
  return getJSON<FindingsResponse>(`/findings?${query.toString()}`);
}

export async function fetchFindingsSummary(filters?: {
  from?: string;
  to?: string;
  severity?: "info" | "warning" | "alert" | "critical";
  detectorID?: string;
}): Promise<FindingsSummaryResponse> {
  const query = new URLSearchParams();
  if (filters?.from) query.set("from", filters.from);
  if (filters?.to) query.set("to", filters.to);
  if (filters?.severity) query.set("severity", filters.severity);
  if (filters?.detectorID) query.set("detector_id", filters.detectorID);
  return getJSON<FindingsSummaryResponse>(`/findings/summary?${query.toString()}`);
}

export async function fetchDashboardSummary(force = false): Promise<DashboardSummaryResponse> {
  return getJSON<DashboardSummaryResponse>(force ? "/dashboard/summary?force=true" : "/dashboard/summary");
}

export async function fetchUserOnboardingStatus(): Promise<UserOnboardingStatus> {
  return getJSON<UserOnboardingStatus>("/user/onboarding");
}

export async function completeUserOnboarding(): Promise<UserOnboardingStatus> {
  return sendJSON<UserOnboardingStatus>("/user/onboarding/complete", "PATCH", {});
}

export async function fetchMoneyFlowSummary(params: { startDate: string; endDate: string }): Promise<MoneyFlowSummaryResponse> {
  const query = new URLSearchParams({
    start_date: params.startDate,
    end_date: params.endDate,
  });
  return getJSON<MoneyFlowSummaryResponse>(`/money-flow/summary?${query.toString()}`);
}

export async function resolveFinding(findingNumber: number) {
  return sendJSON<{ status: string }>(`/findings/${findingNumber}/resolve`, "PATCH");
}

export async function reopenFinding(findingNumber: number) {
  return sendJSON<{ status: string }>(`/findings/${findingNumber}/reopen`, "PATCH");
}

export async function bulkResolveFindings(findingNumbers: number[]) {
  return sendJSON<{ message: string; resolved_count: number }>("/findings/resolve", "PATCH", findingNumbers);
}

export async function fetchTransactions(
  limit = 50,
  offset = 0,
  filters?: {
    startDate?: string;
    endDate?: string;
    direction?: "inflow" | "outflow";
    search?: string;
    payee?: string;
    categoryID?: string;
    accountID?: string;
    tag?: string;
    withFindings?: boolean;
    findingNumber?: number;
    findingStatus?: "all" | "active" | "resolved";
    sortBy?: "date" | "payee" | "amount" | "category_id" | "account_name";
    sortDir?: "asc" | "desc";
    /** Only transactions without a category. */
    uncategorized?: boolean;
    /** The transactions the user deleted (or hid) instead of the live ones. */
    deleted?: boolean;
  }
): Promise<TransactionsResponse> {
  const query = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  if (filters?.startDate) query.set("start_date", filters.startDate);
  if (filters?.endDate) query.set("end_date", filters.endDate);
  if (filters?.direction) query.set("direction", filters.direction);
  if (filters?.search) query.set("search", filters.search);
  if (filters?.payee) query.set("payee", filters.payee);
  if (filters?.categoryID) query.set("category_id", filters.categoryID);
  if (filters?.accountID) query.set("account_id", filters.accountID);
  if (filters?.tag) query.set("tag", filters.tag);
  if (filters?.withFindings) query.set("with_findings", "true");
  if (typeof filters?.findingNumber === "number" && Number.isFinite(filters.findingNumber) && filters.findingNumber > 0) {
    // finding_id is the backend query param name; findingNumber is the camelCase alias in this params type
    query.set("finding_id", String(filters.findingNumber));
  }
  if (filters?.findingStatus) query.set("finding_status", filters.findingStatus);
  if (filters?.sortBy) query.set("sort_by", filters.sortBy);
  if (filters?.sortDir) query.set("sort_dir", filters.sortDir);
  if (filters?.uncategorized) query.set("category", "uncategorized");
  if (filters?.deleted) query.set("deleted", "true");
  return getJSON<TransactionsResponse>(`/transactions?${query.toString()}`);
}

export interface TransactionFilterOptionsResponse {
  accounts: Array<{ id: string; name: string }>;
  tags: Array<{ normalized_name: string }>;
}

export async function fetchTransactionFilterOptions(): Promise<TransactionFilterOptionsResponse> {
  return getJSON<TransactionFilterOptionsResponse>("/transactions/filter-options");
}

// Reserved API surface: consumed by planned dedicated classification pages.
export async function fetchClassifiedTransactions(
  type: "recurring_payment" | "salary" | "credit_card_payment",
  limit = 50,
  offset = 0,
  filters?: {
    startDate?: string;
    endDate?: string;
    payee?: string;
    search?: string;
    sortBy?: "date" | "payee" | "amount" | "category_id" | "detected_at";
    sortDir?: "asc" | "desc";
  }
): Promise<ClassifiedTransactionsResponse> {
  const query = new URLSearchParams({
    type,
    limit: String(limit),
    offset: String(offset),
  });
  if (filters?.startDate) query.set("start_date", filters.startDate);
  if (filters?.endDate) query.set("end_date", filters.endDate);
  if (filters?.payee) query.set("payee", filters.payee);
  if (filters?.search) query.set("search", filters.search);
  if (filters?.sortBy) query.set("sort_by", filters.sortBy);
  if (filters?.sortDir) query.set("sort_dir", filters.sortDir);
  return getJSON<ClassifiedTransactionsResponse>(`/transactions/classifications?${query.toString()}`);
}

export async function refreshTransactionClassifications(userId: string): Promise<{
  message: string;
  recurring_count: number;
  salary_count: number;
}> {
  return sendJSON<{
    message: string;
    recurring_count: number;
    salary_count: number;
  }>(`/admin/users/${userId}/classifications/refresh`, "POST", {});
}

export async function fetchPayees(params?: {
  limit?: number;
  offset?: number;
  search?: string;
  filter?: "all" | "safe" | "flagged" | "watched";
  sortBy?: "display_name" | "tx_count_local" | "complaints_count_local" | "is_safe_effective" | "is_watched" | "created_at";
  sortDir?: "asc" | "desc";
}): Promise<PayeesResponse> {
  const query = new URLSearchParams();
  if (typeof params?.limit === "number") query.set("limit", String(params.limit));
  if (typeof params?.offset === "number") query.set("offset", String(params.offset));
  if (params?.search) query.set("search", params.search);
  if (params?.filter) query.set("filter", params.filter);
  if (params?.sortBy) query.set("sort_by", params.sortBy);
  if (params?.sortDir) query.set("sort_dir", params.sortDir);
  const suffix = query.toString();
  return getJSON<PayeesResponse>(suffix ? `/payees?${suffix}` : "/payees");
}

export async function updatePayeeSafety(id: string, is_safe_override: boolean | null) {
  await sendJSON<{ message: string }>(`/payees/${id}/safe`, "PATCH", { is_safe_override });
}

// Reserved API surface: payee complaint submit UI is intentionally deferred.
export async function createPayeeComplaint(globalPayeeID: string, reason: string, agencyPayeeID?: string) {
  await sendJSON<{ message: string }>("/payees/complaints", "POST", {
    global_payee_id: globalPayeeID,
    agency_payee_id: agencyPayeeID,
    reason,
  });
}

export async function updatePayeeWatch(id: string, is_watched: boolean) {
	await sendJSON<{ message: string }>(`/payees/${id}/watch`, "PATCH", { is_watched });
}

export async function fetchDetectorPrefs(): Promise<Record<string, DetectorPref>> {
  return getJSON<Record<string, DetectorPref>>("/detector/prefs");
}

export async function fetchYNABStatus(): Promise<{
  connected: boolean;
  last_synced_at?: string | null;
}> {
  return getJSON<{
    connected: boolean;
    last_synced_at?: string | null;
  }>("/auth/ynab/status");
}


export async function fetchCategoryAllocation(): Promise<{ allocation: CategoryAllocation | null }> {
  return getJSON<{ allocation: CategoryAllocation | null }>("/categories/allocation");
}

export interface AIGenerationCounter {
  daily_remaining: number;
  cooldown_seconds: number;
}

export type CategoryAISuggestions = {
  save: Record<string, number>;
  balance: Record<string, number>;
  enjoy: Record<string, number>;
};

export async function fetchCategoriesOverview(): Promise<{
  groups: CategoryGroupOverview[];
  allocation: CategoryAllocation | null;
  ai_suggestions: CategoryAISuggestions | null;
  ai_generation: AIGenerationCounter;
  period: { start_date: string; end_date: string };
}> {
  return getJSON<{
    groups: CategoryGroupOverview[];
    allocation: CategoryAllocation | null;
    ai_suggestions: CategoryAISuggestions | null;
    ai_generation: AIGenerationCounter;
    period: { start_date: string; end_date: string };
  }>("/categories/overview");
}

export async function fetchCategoryAISuggestions(groups: CategoryGroupOverview[]): Promise<
  CategoryAISuggestions & { ai_generation: AIGenerationCounter }
> {
  return sendJSON<CategoryAISuggestions & { ai_generation: AIGenerationCounter }>(
    "/categories/ai-suggestions",
    "POST",
    { groups }
  );
}

export async function saveCategoryAllocation(payload: {
  selected_plan: "customize" | "save" | "balance" | "enjoy";
  origin: "user" | "ai";
  ai_mode?: "save" | "balance" | "enjoy";
  allocations: Record<string, number>;
}): Promise<{ allocation: CategoryAllocation }> {
  return sendJSON<{ allocation: CategoryAllocation }>("/categories/allocation", "PUT", payload);
}

export async function submitContact(payload: ContactPayload): Promise<{ status: string }> {
  return sendJSON<{ status: string }>("/contact/dashboard", "POST", payload);
}

export async function fetchNotificationPreferences(): Promise<NotificationPreferences> {
  return getJSON<NotificationPreferences>("/notifications/preferences");
}

export async function updateNotificationPreferences(payload: {
  new_findings: boolean;
  weekly_digest: boolean;
  weekly_ai_summary: boolean;
  monthly_ai_summary: boolean;
  summary_timezone: string;
  summary_weekday: "Sunday" | "Monday";
  summary_time: string;
  product_updates: boolean;
}): Promise<NotificationPreferences> {
  return sendJSON<NotificationPreferences>("/notifications/preferences", "PUT", payload);
}

export async function fetchFormattingPreferences(): Promise<FormattingPreferences> {
  return getJSON<FormattingPreferences>("/user/formatting-preferences");
}

export interface SupportedCurrency {
  code: string;
  name: string;
}

export interface SupportedCurrencyGroups {
  common: SupportedCurrency[];
  all: SupportedCurrency[];
}

export async function fetchSupportedCurrencies(): Promise<SupportedCurrencyGroups> {
  return getJSON<SupportedCurrencyGroups>("/user/formatting-currencies");
}

export async function updateFormattingPreferences(payload: {
  currency: string;
  currency_placement: "before" | "after" | "none";
  number_format: "us" | "eu";
  date_format: "mmm_d_yyyy" | "yyyy_mm_dd" | "dd_mm_yyyy" | "mm_dd_yyyy" | "d_mmm_yyyy";
  hide_amounts: boolean;
}): Promise<FormattingPreferences> {
  return sendJSON<FormattingPreferences>("/user/formatting-preferences", "PUT", payload);
}

export interface YNABSyncLog {
  id: string;
  plan_id: string;
  plan_name: string;
  tx_count: number;
  payee_count: number;
  sync_status: "ok" | "error";
  created_at: string;
}

export async function fetchYNABSyncLogs(limit = 20): Promise<{ logs: YNABSyncLog[] }> {
  return getJSON<{ logs: YNABSyncLog[] }>(`/auth/ynab/sync-logs?limit=${limit}`);
}

export interface YNABPlan {
  id: string;
  name: string;
}

export async function fetchYNABDefaultPlan(): Promise<{ default_plan?: YNABPlan }> {
  return getJSON<{ default_plan?: YNABPlan }>("/ynab/default-plan");
}

export async function fetchYNABAuthorizeURL(): Promise<string> {
  const data = await getJSON<{ url: string }>("/auth/ynab/authorize?mode=json");
  return data.url;
}

export async function disconnectYNAB(): Promise<void> {
  await sendJSON<{ message: string }>("/auth/ynab/disconnect", "POST", {});
}

export async function triggerYNABSync(): Promise<{ status?: string; message?: string }> {
  return sendJSON<{ status?: string; message?: string }>("/sync-ynab", "POST", {});
}

export async function triggerInvestigate(): Promise<void> {
  await sendJSON<{ message: string }>("/investigate", "POST", {});
}

export async function fetchAvailableDetectors(): Promise<DetectorMeta[]> {
  const list = await getJSON<unknown[]>("/detectors");
  return (Array.isArray(list) ? list : []) as DetectorMeta[];
}

export async function updateDetectorPref(detector_id: string, can_invoke: boolean, can_notify: boolean) {
  await sendJSON<{ message: string }>("/detector/prefs/update", "POST", {
    detector_id,
    can_invoke,
    can_notify,
  });
}

export async function fetchGroupedDetectors(): Promise<DetectorGroupItem[]> {
  const list = await getJSON<unknown[]>("/detectors/grouped");
  return (Array.isArray(list) ? list : []) as DetectorGroupItem[];
}

export async function bulkUpdateDetectorPrefs(
  items: { detector_id: string; can_invoke: boolean; can_notify: boolean }[]
) {
  await sendJSON<{ message: string }>("/detector/prefs/bulk", "POST", items);
}

export async function fetchFamilyMembers(): Promise<FamilyResponse> {
  return getJSON<FamilyResponse>("/family/members");
}

// Reserved API surface: family invite/remove remains hidden while Family is in demo mode.
export async function inviteFamilyMember(email: string) {
  return sendJSON<{ id: string }>("/family/invite", "POST", { invited_email: email });
}

export async function removeFamilyMember(userID: string) {
  await sendJSON<{ message: string }>(`/family/members/${userID}`, "DELETE");
}

export async function fetchInvitePreview(token: string): Promise<InvitePreviewResponse> {
  const query = new URLSearchParams({ token });
  return getJSON<InvitePreviewResponse>(`/invite/preview?${query.toString()}`);
}

export async function acceptInvite(token: string): Promise<{ message: string }> {
  return sendJSON<{ message: string }>("/invite/accept", "POST", { token });
}

export async function fetchReferralStats(): Promise<ReferralStats> {
  return getJSON<ReferralStats>("/referral/stats");
}

export async function fetchInvoices(): Promise<Invoice[]> {
  return getJSON<Invoice[]>("/billing/invoices");
}

// createCheckout sends the semantic billing cycle; the backend resolves it to
// the active provider's product id, so the dashboard never needs provider product
// env vars and a provider switch needs no frontend change.
export async function createCheckout(cycle: "monthly" | "yearly"): Promise<string> {
  const data = await sendJSON<{ url: string }>("/billing/create-checkout", "POST", {
    cycle,
  });
  return data.url;
}

export async function createPortal(): Promise<string> {
  const data = await sendJSON<{ url: string }>("/billing/create-portal", "POST", {});
  return data.url;
}

export async function fetchAgencyContext(): Promise<AgencyContext> {
  return getJSON<AgencyContext>("/agency/context");
}

export async function updateUserProfile(name: string, image?: string) {
  return sendJSON<{ id: string; email: string; name: string; image: string }>("/user/profile", "PATCH", {
    name,
    image: image ?? "",
  });
}

export interface UserDeletionStatus {
  pending: boolean;
  deletion_requested_at?: string;
  deletion_deadline_at?: string;
}

export async function fetchUserDeletionStatus(): Promise<UserDeletionStatus> {
  return getJSON<UserDeletionStatus>("/user/deletion-status");
}

export async function requestAccountDeletion(): Promise<{ deletion_deadline_at: string }> {
  return sendJSON<{ deletion_deadline_at: string }>("/user/delete/request", "POST", {});
}

export async function recoverAccount(): Promise<{ message: string; pending: boolean }> {
  return sendJSON<{ message: string; pending: boolean }>("/user/delete/cancel", "POST", {});
}

export async function purgeAccountNow(): Promise<{ message: string; detached_members: number; pending: boolean }> {
  return sendJSON<{ message: string; detached_members: number; pending: boolean }>("/user/delete/purge", "POST", {});
}

export interface FinancialScoreRecord {
  id: string;
  score_value?: number | null;
  detector_score: number;
  notification_score: number;
  ynab_freshness_score: number;
  deviation_score: number;
  stale_findings_score: number;
  stale_findings_count?: number;
  created_at: string;
}

export async function fetchFinancialScore(params?: { limit?: number; offset?: number }): Promise<{ current: FinancialScoreRecord | null; history: FinancialScoreRecord[]; total: number; limit: number; offset: number }> {
  const query = new URLSearchParams();
  if (typeof params?.limit === "number") query.set("limit", String(params.limit));
  if (typeof params?.offset === "number") query.set("offset", String(params.offset));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return getJSON<{ current: FinancialScoreRecord | null; history: FinancialScoreRecord[]; total: number; limit: number; offset: number }>(`/financial-score${suffix}`);
}

export async function refreshFinancialScore(params?: { limit?: number; offset?: number; auto?: boolean }): Promise<{ current: FinancialScoreRecord | null; history: FinancialScoreRecord[]; total: number; limit: number; offset: number }> {
  const query = new URLSearchParams();
  if (typeof params?.limit === "number") query.set("limit", String(params.limit));
  if (typeof params?.offset === "number") query.set("offset", String(params.offset));
  if (params?.auto) query.set("source", "auto");
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return sendJSON<{ current: FinancialScoreRecord | null; history: FinancialScoreRecord[]; total: number; limit: number; offset: number }>(`/financial-score/refresh${suffix}`, "POST", {});
}

export async function fetchAdminStats(): Promise<{
  total_users: number;
  total_connections: number;
  total_findings: number;
  trial: number;
  monthly: number;
  yearly: number;
}> {
  return getJSON<{
    total_users: number;
    total_connections: number;
    total_findings: number;
    trial: number;
    monthly: number;
    yearly: number;
  }>("/admin/stats");
}

export async function fetchAdminUsers(
  limit = 50,
  offset = 0,
): Promise<{
  users: Array<{
    id: string;
    agency_id?: string;
    allow_beta_detector?: boolean;
    see_beta_findings?: boolean;
    enabled_sources?: string[];
    name: string;
    email: string;
    role: string;
    subscription_status?: string;
    trial_ends_at?: string;
    created_at?: string;
  }>;
  total: number;
}> {
  type UserItem = {
    id: string;
    agency_id?: string;
    allow_beta_detector?: boolean;
    see_beta_findings?: boolean;
    enabled_sources?: string[];
    name: string;
    email: string;
    role: string;
    subscription_status?: string;
    trial_ends_at?: string;
    created_at?: string;
  };
  const body = await getJSON<{ users?: UserItem[]; total?: number }>(
    `/admin/users?limit=${limit}&offset=${offset}`,
  );
  return { users: body.users ?? [], total: body.total ?? 0 };
}

export async function adminSetAgencyAllowBetaDetector(payload: {
  agency_id: string;
  allow_beta_detector: boolean;
}): Promise<void> {
  await sendJSON<{ message: string }>("/admin/agency/allow-beta-detector", "POST", payload);
}

/** Opens or closes a beta source (for example "moneyman") for one agency. */
export async function adminSetAgencySourceAccess(payload: { agency_id: string; source: string; enabled: boolean }): Promise<void> {
  await sendJSON<{ agency_id: string }>("/admin/agency/source-access", "POST", payload);
}

export async function adminSetAgencySeeBetaFindings(payload: {
  agency_id: string;
  see_beta_findings: boolean;
}): Promise<void> {
  await sendJSON<{ message: string }>("/admin/agency/see-beta-findings", "POST", payload);
}

export async function adminSetSubscriptionStatus(payload: {
  agency_id: string;
  status: "beta" | "trial";
  reason?: string;
  trial_days?: number;
}): Promise<void> {
  await sendJSON<{ message: string }>("/admin/subscription/status", "POST", payload);
}

export async function adminExtendTrial(payload: {
  agency_id: string;
  extra_days: number;
  reason?: string;
}): Promise<void> {
  await sendJSON<{ message: string }>("/admin/subscription/trial/extend", "POST", payload);
}

export async function adminForceDeleteUser(payload: { user_id: string }): Promise<void> {
  await sendJSON<{ message: string }>("/admin/users/force-delete", "POST", payload);
}

export async function adminInviteUser(payload: { name: string; email: string }): Promise<{ message: string }> {
  return sendJSON<{ message: string }>("/admin/users/invite", "POST", payload);
}

export async function adminSeedUser(userId: string, payload?: { seed_key?: string; mode?: "six_months" | "day"; months?: number; days?: number; clear_existing_seed?: boolean }): Promise<{ message: string; tx_count: number; seed_key?: string }> {
  return sendJSON<{ message: string; tx_count: number; seed_key?: string }>(`/admin/users/${userId}/seed`, "POST", payload ?? {});
}

export async function adminDeleteSeedData(userId: string): Promise<{ message: string; deleted_rows: number }> {
  return sendJSON<{ message: string; deleted_rows: number }>(`/admin/users/${userId}/seed`, "DELETE", {});
}

export type AdminDigestPeriod = "daily" | "weekly" | "monthly";

export async function adminSendUserDigest(
  userId: string,
  payload: { period: AdminDigestPeriod; include_ai: boolean },
): Promise<{ message: string; findings_count: number; ai_summary_enabled: boolean }> {
  return sendJSON<{ message: string; findings_count: number; ai_summary_enabled: boolean }>(
    `/admin/users/${userId}/digest-test`,
    "POST",
    payload,
  );
}

export interface DetectorFindingStat {
  detector_id: string;
  name: string;
  total_findings: number;
  agencies_enabled: number;
}

export interface AdminFindingsCountResponse {
  total: number;
  active: number;
  resolved: number;
  beta: number;
  silent: number;
  loud: number;
  by_detector: DetectorFindingStat[];
}

export async function fetchAdminFindingsCount(): Promise<AdminFindingsCountResponse> {
  const data = await getJSON<Partial<AdminFindingsCountResponse>>("/admin/findings/count");
  return {
    total: data.total ?? 0,
    active: data.active ?? 0,
    resolved: data.resolved ?? 0,
    beta: data.beta ?? 0,
    silent: data.silent ?? 0,
    loud: data.loud ?? 0,
    by_detector: data.by_detector ?? [],
  };
}

export async function fetchAdminTags(): Promise<Array<{ id: string; agency_id?: string; name: string; normalized_name: string }>> {
	const data = await getJSON<{ tags?: Array<{ id: string; agency_id?: string; name: string; normalized_name: string }> }>("/admin/tags");
	return data.tags ?? [];
}

export async function createAdminTag(name: string): Promise<{ id: string; agency_id?: string; name: string; normalized_name: string }> {
	return sendJSON<{ id: string; agency_id?: string; name: string; normalized_name: string }>("/admin/tags", "POST", { name });
}

export async function deleteAdminTag(id: string): Promise<{ message: string }> {
	return sendJSON<{ message: string }>(`/admin/tags/${id}`, "DELETE", {});
}

export async function fetchAdminDetectors(): Promise<Array<{
  id: string;
  name?: string;
  description?: string;
  schedule?: string;
  severity?: "info" | "warning" | "alert" | "critical";
  tier?: string;
  category?: string;
  is_enabled?: boolean;
  is_silent?: boolean;
  is_beta?: boolean;
}>> {
  const list = await getJSON<unknown[]>("/admin/detectors");
  return (Array.isArray(list) ? list : []) as Array<{
    id: string;
    name?: string;
    description?: string;
    schedule?: string;
    severity?: "info" | "warning" | "alert" | "critical";
    tier?: string;
    category?: string;
    is_enabled?: boolean;
    is_silent?: boolean;
    is_beta?: boolean;
  }>;
}

export async function updateAdminDetectorConfig(payload: {
  detector_id: string;
  is_enabled: boolean;
  is_silent: boolean;
  is_beta: boolean;
}): Promise<void> {
  await sendJSON<{ message: string }>("/admin/detectors/update", "POST", payload);
}

export async function fetchAdminGlobalPayees(
  params: {
    limit?: number;
    offset?: number;
    search?: string;
    safety?: "all" | "safe" | "flagged";
    sortBy?: string;
    sortDir?: "asc" | "desc";
  } = {},
): Promise<{
  payees: Array<{
    id: string;
    canonical_name: string;
    normalized_name: string;
    provider: string;
    is_safe_global: boolean;
    complaints_count_global: number;
    tx_count_global: number;
  }>;
  total: number;
  searchTotal: number;
  safeCount: number;
  flaggedCount: number;
}> {
  type PayeeItem = {
    id: string;
    canonical_name: string;
    normalized_name: string;
    provider: string;
    is_safe_global: boolean;
    complaints_count_global: number;
    tx_count_global: number;
  };
  const {
    limit = 50,
    offset = 0,
    search = "",
    safety = "all",
    sortBy = "tx_count_global",
    sortDir = "desc",
  } = params;
  const qs = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
    safety,
    sort_by: sortBy,
    sort_dir: sortDir,
  });
  if (search.trim() !== "") qs.set("search", search.trim());
  const body = await getJSON<{
    payees?: PayeeItem[];
    total?: number;
    search_total?: number;
    safe_count?: number;
    flagged_count?: number;
  }>(`/admin/global-payees?${qs.toString()}`);
  return {
    payees: body.payees ?? [],
    total: body.total ?? 0,
    searchTotal: body.search_total ?? 0,
    safeCount: body.safe_count ?? 0,
    flaggedCount: body.flagged_count ?? 0,
  };
}

export async function updateAdminGlobalPayeeSafe(id: string, safe: boolean): Promise<void> {
  await sendJSON<{ message: string }>(`/admin/payees/global/${id}/safe`, "PATCH", { safe });
}

// --- AI Usage & Controls ---

export interface AIAppSetting {
  key: string;
  value: string;
  value_type: string;
  description?: string;
  updated_at: string;
  updated_by?: string;
}

export interface AIModelPricing {
  provider: string;
  model: string;
  input_cost_per_1m_tokens_usd: number;
  output_cost_per_1m_tokens_usd: number;
  effective_from: string;
  effective_to?: string;
}

export interface AIUsageSummary {
  total_requests: number;
  success_count: number;
  error_count: number;
  blocked_count: number;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  estimated_cost_usd: number;
}

export interface AIUsageByFeature {
  feature: string;
  request_count: number;
  success_count: number;
  error_count: number;
  total_tokens: number;
  estimated_cost_usd: number;
}

export interface AIUsageByModel {
  model: string;
  request_count: number;
  total_tokens: number;
  estimated_cost_usd: number;
}

export interface AIUsageByDay {
  usage_date: string;
  request_count: number;
  success_count: number;
  error_count: number;
  total_tokens: number;
  estimated_cost_usd: number;
}

export interface AIUsageByUser {
  user_id?: string;
  request_count: number;
  total_tokens: number;
  estimated_cost_usd: number;
}

export interface AIGoogleSnapshot {
  id: string;
  snapshot_date: string;
  created_at: string;
  provider: string;
  source: string;
  project_id: string;
  reported_requests?: number;
  reported_input_tokens?: number;
  reported_output_tokens?: number;
  reported_total_tokens?: number;
  reported_cost_usd?: number;
}

export interface AIUsageSummaryResponse {
  range_summary: AIUsageSummary;
  today_summary: AIUsageSummary;
  mtd_summary: AIUsageSummary;
  health_status: "green" | "yellow" | "red";
  usage_percent_of_budget: number;
  monthly_budget_usd: number;
  projected_monthly_cost: number;
  llm_enabled: boolean;
  latest_google_snapshot?: AIGoogleSnapshot;
}

export type AIRange = "today" | "7d" | "30d" | "90d" | "month" | `${number}d`;

export async function fetchAdminAISettings(): Promise<{
  settings: AIAppSetting[];
  pricing: AIModelPricing[];
}> {
  return getJSON<{ settings: AIAppSetting[]; pricing: AIModelPricing[] }>("/admin/ai/settings");
}

export async function updateAdminAISettings(settings: Array<{ key: string; value: string }>): Promise<void> {
  await sendJSON<{ message: string }>("/admin/ai/settings", "PUT", { settings });
}

export async function fetchAdminAIUsageSummary(range: AIRange = "30d"): Promise<AIUsageSummaryResponse> {
  return getJSON<AIUsageSummaryResponse>(`/admin/ai/usage/summary?range=${range}`);
}

export async function fetchAdminAIUsageByFeature(range: AIRange = "30d"): Promise<{ data: AIUsageByFeature[] }> {
  return getJSON<{ data: AIUsageByFeature[] }>(`/admin/ai/usage/by-feature?range=${range}`);
}

export async function fetchAdminAIUsageByModel(range: AIRange = "30d"): Promise<{ data: AIUsageByModel[] }> {
  return getJSON<{ data: AIUsageByModel[] }>(`/admin/ai/usage/by-model?range=${range}`);
}

export async function fetchAdminAIUsageByUser(range: AIRange = "30d"): Promise<{ data: AIUsageByUser[] }> {
  return getJSON<{ data: AIUsageByUser[] }>(`/admin/ai/usage/by-user?range=${range}`);
}

export async function fetchAdminAIUsageByDay(range: AIRange = "30d"): Promise<{ data: AIUsageByDay[] }> {
  return getJSON<{ data: AIUsageByDay[] }>(`/admin/ai/usage/by-day?range=${range}`);
}

export async function adminReconcileGoogleUsage(): Promise<{ message: string; snapshot: AIGoogleSnapshot }> {
  return sendJSON<{ message: string; snapshot: AIGoogleSnapshot }>("/admin/ai/reconcile-google-usage", "POST", {});
}

export interface UserAIUsageResponse {
  range_summary: AIUsageSummary;
  today_summary: AIUsageSummary;
  month_summary: AIUsageSummary;
  by_feature: AIUsageByFeature[];
  last_event?: {
    id: string;
    created_at: string;
    feature: string;
    model: string;
    status: string;
    error_message?: string;
    total_tokens: number;
    estimated_cost_usd: number;
  };
}

export async function fetchAdminUserAIUsage(userId: string, range: AIRange = "30d"): Promise<UserAIUsageResponse> {
  return getJSON<UserAIUsageResponse>(`/admin/users/${userId}/ai-usage?range=${range}`);
}


// ── Connections (bank feeds and budgeting apps) ──────────────────────────────

export type ConnectionKind = "feed" | "budget";
export type ConnectionProvider = "ynab" | "simplefin" | "webhook" | "moneyman" | "lunchmoney" | "plaid";
export type ConnectionStatus = "pending" | "active" | "needs_reauth" | "error" | "paused" | "disconnected";

export interface Integration {
  id: string;
  provider: ConnectionProvider;
  kind: ConnectionKind;
  display_name: string;
  status: ConnectionStatus;
  institution_name?: string;
  secret_prefix?: string;
  /** Webhook: when and from which IP address the key was last used. */
  last_ingest_at?: string;
  last_ingest_ip?: string;
  last_success_at?: string | null;
  last_error_code?: string;
  last_error_message?: string;
  created_at: string;
  disconnected_at?: string | null;
}

export interface IntegrationsResponse {
  /** "budget" when a budgeting app is connected, "feed" for bank feeds, else "none". */
  mode: "budget" | "feed" | "none";
  policy: { can_connect: boolean; max_feeds: number; plaid_allowed: boolean };
  feed_count: number;
  /** Only agency owners can connect, reconnect or disconnect sources. */
  is_owner: boolean;
  /** Which sources this account may connect right now. */
  providers: Partial<Record<ConnectionProvider, boolean>>;
  connections: Integration[];
}

export interface IntegrationAccount {
  id: string;
  external_account_id: string;
  /** What Fintective shows: your own name when you gave one, else the name the source reports. */
  name: string;
  reported_name?: string;
  custom_name?: string;
  type: string;
  mask?: string;
  currency: string;
  include: boolean;
  exclude_reason?: "currency" | "possible_duplicate" | "user" | "unsupported_type" | "";
}

export interface ConnectResult {
  connection: Integration;
  accounts?: IntegrationAccount[];
  imported?: number;
  warnings: string[];
  /** Set when the connection was saved but its first sync failed. */
  sync_error?: string;
}

export async function fetchIntegrations(): Promise<IntegrationsResponse> {
  return getJSON<IntegrationsResponse>("/integrations");
}

export async function fetchIntegration(id: string): Promise<{ connection: Integration; accounts: IntegrationAccount[] }> {
  return getJSON<{ connection: Integration; accounts: IntegrationAccount[] }>(`/integrations/${encodeURIComponent(id)}`);
}

export async function connectSimpleFIN(setupToken: string, displayName?: string): Promise<ConnectResult> {
  return sendJSON<ConnectResult>("/integrations/simplefin", "POST", { setup_token: setupToken, display_name: displayName });
}

export async function connectLunchMoney(accessToken: string, displayName?: string): Promise<ConnectResult> {
  return sendJSON<ConnectResult>("/integrations/lunchmoney", "POST", { access_token: accessToken, display_name: displayName });
}

export async function reconnectIntegration(id: string, secret: { setup_token?: string; access_token?: string }): Promise<ConnectResult> {
  return sendJSON<ConnectResult>(`/integrations/${encodeURIComponent(id)}/reconnect`, "POST", secret);
}

export interface WebhookKeyResult {
  connection: Integration;
  /** Shown once: only a hash is stored. */
  key: string;
  endpoint: string;
}

export async function createWebhookConnection(displayName?: string): Promise<WebhookKeyResult> {
  return sendJSON<WebhookKeyResult>("/integrations/webhook", "POST", { display_name: displayName });
}

export async function createMoneymanConnection(displayName?: string): Promise<WebhookKeyResult> {
  return sendJSON<WebhookKeyResult>("/integrations/moneyman", "POST", { display_name: displayName });
}

export async function rotateWebhookKey(id: string): Promise<WebhookKeyResult> {
  return sendJSON<WebhookKeyResult>(`/integrations/${encodeURIComponent(id)}/rotate-key`, "POST", {});
}

export async function syncIntegration(id: string): Promise<{ message: string }> {
  return sendJSON<{ message: string }>(`/integrations/${encodeURIComponent(id)}/sync`, "POST", {});
}

export async function disconnectIntegration(id: string, deleteData: boolean): Promise<{ data_deleted: boolean; transactions_deleted: number }> {
  return sendJSON<{ data_deleted: boolean; transactions_deleted: number }>(
    `/integrations/${encodeURIComponent(id)}?delete_data=${deleteData ? "true" : "false"}`,
    "DELETE",
  );
}

export async function setIntegrationAccountIncluded(id: string, accountID: string, include: boolean): Promise<{ include: boolean }> {
  return sendJSON<{ include: boolean }>(`/integrations/${encodeURIComponent(id)}/accounts/${encodeURIComponent(accountID)}`, "PATCH", { include });
}

/** Gives an account your own name; an empty name goes back to the one the source reports. */
export async function renameIntegrationAccount(id: string, accountID: string, name: string): Promise<{ name: string }> {
  return sendJSON<{ name: string }>(`/integrations/${encodeURIComponent(id)}/accounts/${encodeURIComponent(accountID)}`, "PATCH", { name });
}

// ── Fintective categories (bank-feed agencies) ───────────────────────────────

export interface LedgerCategory {
  id: string;
  group_id: string;
  group_name: string;
  name: string;
  origin: "system" | "user";
  is_income: boolean;
  internal: boolean;
  sort_order: number;
  archived: boolean;
  tx_count: number;
}

export async function fetchLedgerCategories(includeArchived = false): Promise<{ categories: LedgerCategory[] }> {
  return getJSON<{ categories: LedgerCategory[] }>(`/categories/items${includeArchived ? "?include_archived=true" : ""}`);
}

export async function createLedgerCategory(name: string, groupID: string): Promise<LedgerCategory> {
  return sendJSON<LedgerCategory>("/categories/items", "POST", { name, group_id: groupID });
}

export async function updateLedgerCategory(
  id: string,
  patch: { name?: string; group_id?: string; sort_order?: number },
): Promise<LedgerCategory> {
  return sendJSON<LedgerCategory>(`/categories/items/${encodeURIComponent(id)}`, "PATCH", patch);
}

export async function setLedgerCategoryArchived(id: string, archived: boolean): Promise<LedgerCategory> {
  return sendJSON<LedgerCategory>(`/categories/items/${encodeURIComponent(id)}/${archived ? "archive" : "unarchive"}`, "POST", {});
}

/** reassignTo is a category id, or "uncategorized". */
export async function deleteLedgerCategory(id: string, reassignTo: string): Promise<{ message: string }> {
  return sendJSON<{ message: string }>(`/categories/items/${encodeURIComponent(id)}?reassign_to=${encodeURIComponent(reassignTo)}`, "DELETE");
}

// ── Transaction edits ────────────────────────────────────────────────────────

/** A null category clears it. */
export async function setTransactionCategory(id: string, categoryID: string | null): Promise<{ updated: number }> {
  return sendJSON<{ updated: number }>(`/transactions/${encodeURIComponent(id)}`, "PATCH", { category_id: categoryID });
}

export async function bulkSetTransactionCategory(ids: string[], categoryID: string | null): Promise<{ updated: number }> {
  return sendJSON<{ updated: number }>("/transactions/bulk-category", "POST", { ids, category_id: categoryID });
}

export async function deleteTransaction(id: string): Promise<{ updated: number }> {
  return sendJSON<{ updated: number }>(`/transactions/${encodeURIComponent(id)}`, "DELETE");
}

export async function bulkDeleteTransactions(ids: string[]): Promise<{ updated: number }> {
  return sendJSON<{ updated: number }>("/transactions/bulk-delete", "POST", { ids });
}

export async function restoreTransaction(id: string): Promise<{ updated: number }> {
  return sendJSON<{ updated: number }>(`/transactions/${encodeURIComponent(id)}/restore`, "POST", {});
}

// ── Admin: source rollout switches ───────────────────────────────────────────

export type SourceFlagValue = "off" | "on";

export async function fetchSourceFlags(): Promise<{ flags: Record<string, SourceFlagValue> }> {
  return getJSON<{ flags: Record<string, SourceFlagValue> }>("/admin/source-flags");
}

export async function setSourceFlag(key: string, value: SourceFlagValue): Promise<{ key: string; value: SourceFlagValue }> {
  return sendJSON<{ key: string; value: SourceFlagValue }>("/admin/source-flags", "PUT", { key, value });
}
